import { useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import { studentsApi } from '../services/api';
import { Student } from '../types';

interface ImportRow {
  name: string;
  phone: string;
  class: string;
  error?: string;
}

interface Props {
  onClose: () => void;
  onImported: (students: Student[]) => void;
}

export const ImportStudentsModal = ({ onClose, onImported }: Props) => {
  const [rows, setRows] = useState<ImportRow[]>([]);
  const [fileName, setFileName] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveResults, setSaveResults] = useState<Array<{ index: number; success: boolean; error?: string }> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const parseFile = (file: File) => {
    setFileName(file.name);
    setSaveResults(null);
    setSaveError('');

    const reader = new FileReader();
    reader.onload = (e) => {
      const data = e.target?.result;
      const workbook = XLSX.read(data, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const raw: Array<Record<string, string>> = XLSX.utils.sheet_to_json(sheet, { defval: '' });

      const parsed: ImportRow[] = raw.map((r) => {
        // Normalize headers: trim, lowercase
        const normalized: Record<string, string> = {};
        for (const key of Object.keys(r)) {
          normalized[key.trim().toLowerCase()] = String(r[key]).trim();
        }

        const name = normalized['name'] || normalized['họ và tên'] || normalized['họ tên'] || '';
        const rawPhone = normalized['phone'] || normalized['phone_number'] || normalized['số điện thoại'] || normalized['sdt'] || normalized['sđt'] || '';
        const phone = rawPhone && !rawPhone.startsWith('0') ? '0' + rawPhone : rawPhone;
        const cls = normalized['class'] || normalized['lớp'] || '';

        const errors: string[] = [];
        if (!name) errors.push('missing name');
        if (!phone) errors.push('missing phone');

        return { name, phone, class: cls, error: errors.length ? errors.join(', ') : undefined };
      });

      setRows(parsed);
    };
    reader.readAsArrayBuffer(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) parseFile(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) parseFile(file);
  };

  const validRows = rows.filter((r) => !r.error);
  const invalidRows = rows.filter((r) => r.error);

  const handleSave = async () => {
    if (validRows.length === 0) return;
    setSaving(true);
    setSaveError('');
    setSaveResults(null);

    try {
      const payload = validRows.map((r) => ({ name: r.name, phone_number: r.phone, class: r.class || undefined }));
      const res = await studentsApi.bulkCreate(payload);
      const results = res.data.results;
      setSaveResults(results);

      const created = results.filter((r) => r.success).map((r) => r.student as Student);
      if (created.length > 0) {
        onImported(created);
      }
    } catch (err) {
      setSaveError((err as { response?: { data?: { error?: string } } }).response?.data?.error || 'Failed to import students');
    } finally {
      setSaving(false);
    }
  };

  const allSaved = saveResults !== null && saveResults.every((r) => r.success);

  return (
    <div
      className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50"
      onClick={onClose}
    >
      <div
        className="relative top-10 mx-auto p-6 border max-w-3xl shadow-lg rounded-md bg-white mb-10"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">Import Students from File</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold leading-none">&times;</button>
        </div>

        <p className="text-sm text-gray-500 mb-4">
          Upload a <strong>.xlsx</strong> or <strong>.csv</strong> file with columns: <code className="bg-gray-100 px-1 rounded">name</code>, <code className="bg-gray-100 px-1 rounded">phone</code>, <code className="bg-gray-100 px-1 rounded">class</code>. The first row must be the header.
        </p>

        {/* Drop zone */}
        {rows.length === 0 && (
          <div
            className="border-2 border-dashed border-gray-300 rounded-lg p-10 text-center cursor-pointer hover:border-blue-400 transition-colors"
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            onClick={() => inputRef.current?.click()}
          >
            <svg className="mx-auto mb-3 w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            <p className="text-gray-500 text-sm">Drag &amp; drop your file here, or <span className="text-blue-600 underline">browse</span></p>
            <p className="text-gray-400 text-xs mt-1">Supports .xlsx and .csv</p>
            <input ref={inputRef} type="file" accept=".xlsx,.csv" className="hidden" onChange={handleFileChange} />
          </div>
        )}

        {/* File loaded — show preview */}
        {rows.length > 0 && (
          <>
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm text-gray-600">
                <span className="font-medium">{fileName}</span> &mdash; {rows.length} row(s) found,{' '}
                <span className="text-green-600 font-medium">{validRows.length} valid</span>
                {invalidRows.length > 0 && (
                  <span className="text-red-500 font-medium">, {invalidRows.length} invalid</span>
                )}
              </div>
              <button
                className="text-sm text-blue-600 hover:underline"
                onClick={() => { setRows([]); setFileName(''); setSaveResults(null); setSaveError(''); if (inputRef.current) inputRef.current.value = ''; }}
              >
                Change file
              </button>
            </div>

            <div className="max-h-80 overflow-y-auto border rounded-lg">
              <table className="min-w-full text-sm divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Class</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {rows.map((row, i) => {
                    const result = saveResults?.find((r) => r.index === i);
                    return (
                      <tr key={i} className={row.error ? 'bg-red-50' : result?.success ? 'bg-green-50' : result && !result.success ? (result.error?.includes('already exists') ? 'bg-yellow-50' : 'bg-red-50') : ''}>
                        <td className="px-4 py-2 text-gray-400">{i + 1}</td>
                        <td className="px-4 py-2 text-gray-900">{row.name || <span className="text-red-400 italic">empty</span>}</td>
                        <td className="px-4 py-2 text-gray-900">{row.phone || <span className="text-red-400 italic">empty</span>}</td>
                        <td className="px-4 py-2 text-gray-900">{row.class || '-'}</td>
                        <td className="px-4 py-2">
                          {row.error ? (
                            <span className="text-red-500 text-xs">{row.error}</span>
                          ) : result?.success ? (
                            <span className="text-green-600 text-xs font-medium">Saved</span>
                          ) : result && !result.success ? (
                            <span className={`text-xs ${result.error?.includes('already exists') ? 'text-yellow-600' : 'text-red-500'}`}>
                              {result.error}
                            </span>
                          ) : (
                            <span className="text-green-600 text-xs">OK</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {invalidRows.length > 0 && (
              <p className="text-xs text-red-500 mt-2">
                Rows with errors will be skipped. Only {validRows.length} valid row(s) will be imported.
              </p>
            )}
          </>
        )}

        {saveError && (
          <div className="mt-3 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
            {saveError}
          </div>
        )}

        {allSaved && (
          <div className="mt-3 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded text-sm">
            All students imported successfully!
          </div>
        )}

        <div className="flex justify-end space-x-3 mt-5">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
          >
            {allSaved ? 'Close' : 'Cancel'}
          </button>
          {!allSaved && (
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || validRows.length === 0}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : `Save ${validRows.length} Student(s)`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
